"use client";
import { scrollToSection } from "@/lib/utils";
import { motion, useAnimationControls } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useCallback } from "react";

const ServicesSection = () => {
  const controls = useAnimationControls();
  const uiuxControls = useAnimationControls();
  const productDesignControls = useAnimationControls();
  const webDevelopmentControls = useAnimationControls();
  const brandIdentityControls = useAnimationControls();
  const iosAndroidAppsControls = useAnimationControls();
  const prototypingTestingControls = useAnimationControls();
  const designSystemsControls = useAnimationControls();
  const startAnimation = useCallback(
    async (isMounted: { current: boolean }) => {
      while (isMounted.current) {
        await Promise.all([
          controls.start({
            y: "-40%",
            transition: { duration: 5, ease: "easeInOut" },
          }),
          uiuxControls.start({
            x: "730%",
            y: "190%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.2 },
          }),
          productDesignControls.start({
            x: "570%",
            y: "530%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.2 },
          }),
          webDevelopmentControls.start({
            x: "520%",
            y: "-350%",
            transition: { duration: 5, ease: "easeInOut", delay: 0 },
          }),
          brandIdentityControls.start({
            x: "635%",
            y: "-350%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.4 },
          }),
          iosAndroidAppsControls.start({
            x: "-470%",
            y: "390%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.3 },
          }),
          prototypingTestingControls.start({
            x: "-475%",
            y: "-220%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.1 },
          }),
          designSystemsControls.start({
            x: "-530%",
            y: "-240%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.5 },
          }),
        ]);
        if (!isMounted.current) return;
        await new Promise((resolve) => setTimeout(resolve, 1500));
        await Promise.all([
          controls.start({
            y: "-180%",
            transition: { duration: 5, ease: "easeInOut" },
          }),
          productDesignControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.3 },
          }),
          uiuxControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.3 },
          }),
          webDevelopmentControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.1 },
          }),
          brandIdentityControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.2 },
          }),
          iosAndroidAppsControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.4 },
          }),
          prototypingTestingControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0.2 },
          }),
          designSystemsControls.start({
            x: "0%",
            y: "0%",
            transition: { duration: 5, ease: "easeInOut", delay: 0 },
          }),
        ]);
        if (!isMounted.current) return;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    },
    [
      controls,
      uiuxControls,
      webDevelopmentControls,
      brandIdentityControls,
      iosAndroidAppsControls,
      prototypingTestingControls,
      designSystemsControls,
      productDesignControls,
    ]
  );

  useEffect(() => {
    const isMounted = { current: true };
    const timer = setTimeout(() => {
      startAnimation(isMounted);
    }, 2000);
    return () => {
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, [startAnimation]);

  return (
    <div className="relative hidden sm:block max-w-[1357px] mx-auto my-6 rounded-3xl h-[350px] overflow-hidden">
      <motion.div
        animate={controls}
        className="w-full h-full"
        initial={{ y: "-180%" }}
        style={{ translateY: "0%" }}
      >
        <Image
          src="/images/services.png"
          width={1000}
          height={1000}
          alt="Services background"
          className="w-[100vw] object-cover"
          priority
        />
      </motion.div>
      <div className="absolute inset-0 z-10 text-[15px] flex items-center justify-between px-32">
        {/* Left ladder */}
        <div className="flex flex-col gap-8 items-start text-white">
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2 ml-20"
            animate={productDesignControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            Product Design
          </motion.div>
          <motion.div
            className="rounded-full border border-white text-primary bg-white backdrop-blur-[27.5px] px-4 py-2"
            animate={uiuxControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            UI/UX Design
          </motion.div>
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2 ml-12"
            animate={webDevelopmentControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            Web Development
          </motion.div>
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2 ml-32"
            animate={brandIdentityControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            Brand Identity
          </motion.div>
        </div>

        {/* Right ladder */}
        <div className="flex flex-col gap-12 items-end text-white">
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2"
            animate={iosAndroidAppsControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            IOS & Android Apps
          </motion.div>
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2 mr-12"
            animate={prototypingTestingControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            Prototyping & Testing
          </motion.div>
          <motion.div
            className="rounded-full border border-white/50 text-primary bg-white backdrop-blur-[27.5px] px-4 py-2 mr-32"
            animate={designSystemsControls}
            initial={{ x: "0%", y: "0%" }}
            whileHover={{ scale: 1.05 }}
          >
            Design Systems
          </motion.div>
        </div>
      </div>
      <div
        onClick={() => scrollToSection("what-we-do")}
        className="absolute inset-0 z-10 flex items-end justify-end p-4"
      >
        <div className="group bg-white h-12 w-12 cursor-pointer relative -left-8 -top-2 font-extralight text-primary rounded-full flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 rounded-full bg-primary-sky transform translate-x-[-100%] translate-y-[100%] transition-transform duration-300 ease-in-out group-hover:translate-x-0 group-hover:translate-y-0" />
          <ArrowUpRight
            className="text-black absolute transform transition-transform duration-300 ease-in-out group-hover:translate-x-[100%] group-hover:translate-y-[-100%] h-12"
            strokeWidth={1.4}
          />
          <ArrowUpRight
            className="text-black absolute transform translate-x-[-100%] translate-y-[100%] transition-transform duration-300 ease-in-out group-hover:translate-x-0 group-hover:translate-y-0 h-12"
            strokeWidth={1.4}
          />
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;
